import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { absoluteUrl, getRouteSeo } from "@/seo/seoConfig";

type MetaAttr = "name" | "property";

function upsertMeta(attr: MetaAttr, key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Route-aware head manager.
 *
 * It keeps exactly one managed tag per name/property/rel and rewrites every
 * managed value on each route change so metadata from a previous route can
 * never leak into the next one. It never touches the visible UI.
 */
export default function SeoHeadManager(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getRouteSeo(pathname);
    const canonical = absoluteUrl(seo.canonicalPath);

    // Core document metadata
    document.title = seo.title;
    document.documentElement.setAttribute("lang", seo.lang);

    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("name", "robots", seo.robots);
    upsertCanonical(canonical);

    // Open Graph
    upsertMeta("property", "og:title", seo.ogTitle);
    upsertMeta("property", "og:description", seo.ogDescription);
    upsertMeta("property", "og:image", seo.ogImage);
    upsertMeta("property", "og:image:alt", seo.ogImageAlt);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", seo.ogType);
    upsertMeta("property", "og:site_name", seo.ogSiteName);
    upsertMeta("property", "og:locale", seo.ogLocale);

    // Twitter Card
    upsertMeta("name", "twitter:card", seo.twitterCard);
    upsertMeta("name", "twitter:title", seo.twitterTitle);
    upsertMeta("name", "twitter:description", seo.twitterDescription);
    upsertMeta("name", "twitter:image", seo.twitterImage);
    upsertMeta("name", "twitter:image:alt", seo.twitterImageAlt);
  }, [pathname]);

  return null;
}